// Barcode-Ablauf: scannen → Open Food Facts → anlegen/zusammenführen/buchen.
import { shortenName, toBaseSize } from '@speis/shared';
import type { ProductView } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { refreshExpiring } from '../core/sse.js';
import { state, upsertProduct } from '../core/store.js';
import { clear, el } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { toast } from '../ui/toast.js';
import { openAddStockSheet, refreshProducts } from '../screens/pantry.js';
import { scanBarcode } from './scanner.js';

interface LookupResult {
  existing: ProductView | null;
  off: {
    name: string | null;
    brand: string | null;
    packageSizeValue: number | null;
    packageSizeUnit: string | null;
    category: string | null;
    imageUrl: string | null;
  } | null;
  mergeCandidates: (ProductView & { score: number })[];
}

export async function openBarcodeFlow(): Promise<void> {
  const code = await scanBarcode();
  if (!code) return;
  await handleCode(code);
}

/** Mehrfach-Scan: ganzer Einkauf am Stück, ohne die Kamera zu schließen. */
export async function openMultiScanFlow(): Promise<void> {
  const queue: string[] = [];
  await scanBarcode({
    title: 'Einkauf einscannen',
    hint: 'Einen Artikel nach dem anderen – die Kamera bleibt offen.',
    continuous: true,
    onCode: (code) => {
      if (!queue.includes(code)) queue.push(code);
    },
  });
  if (!queue.length) return;
  toast(`${queue.length} Artikel gescannt – jetzt der Reihe nach`);
  for (const code of queue) await handleCode(code);
}

async function handleCode(code: string): Promise<void> {
  const result = await guard(
    () => api<LookupResult>(`/api/products/lookup?barcode=${encodeURIComponent(code)}`),
    { failure: 'Nachschlagen fehlgeschlagen' },
  );
  if (!result) return;

  if (result.existing) {
    openAddStockSheet(result.existing);
    return;
  }
  openResolution(code, result);
}

function openResolution(code: string, result: LookupResult): void {
  const off = result.off;

  const name = el('input', {
    class: 'input',
    type: 'text',
    value: off?.name ? shortenName(off.name) : '',
    placeholder: 'Produktname',
  });
  const brand = el('input', {
    class: 'input',
    type: 'text',
    value: off?.brand ?? '',
    placeholder: 'Marke (optional)',
  });
  const sizeValue = el('input', {
    class: 'input',
    type: 'text',
    inputmode: 'decimal',
    value: off?.packageSizeValue ?? '',
    placeholder: 'z.B. 1000',
  });
  const sizeUnit = el('select', { class: 'select' });
  for (const u of ['', 'Stück', 'ml', 'l', 'g', 'kg']) {
    const option = el('option', { value: u }, u || '—');
    if (u === off?.packageSizeUnit) option.selected = true;
    sizeUnit.append(option);
  }

  const body = el('div', {}, [
    off?.imageUrl ? el('img', { class: 'resolve-img', src: off.imageUrl, alt: '' }) : null,
    off
      ? null
      : el(
          'p',
          { class: 'notice notice-warn' },
          'Dieser Barcode steht nicht in der offenen Lebensmitteldatenbank. Bitte kurz selbst ausfüllen – beim nächsten Mal erkennt die App ihn.',
        ),
    el('div', { class: 'field' }, [el('label', {}, 'Name'), name]),
    el('div', { class: 'field' }, [el('label', {}, 'Marke'), brand]),
    el('div', { class: 'field' }, [
      el('label', {}, 'Packungsgröße'),
      el('div', { class: 'input-row' }, [sizeValue, sizeUnit]),
    ]),
  ]);

  if (result.mergeCandidates.length) {
    const list = el('div', { class: 'results' });
    for (const candidate of result.mergeCandidates) {
      list.append(
        el(
          'button',
          {
            class: 'result',
            type: 'button',
            onclick: guarded(
              async () => {
                // Statt ein Duplikat anzulegen: den Barcode am bestehenden
                // Produkt hinterlegen und es damit bestätigen.
                await api(`/api/products/${candidate.id}`, {
                  method: 'PATCH',
                  body: { barcode: code, confirmed: true },
                });
                sheet.close();
                await refreshProducts();
                const product = state.products.find((p) => p.id === candidate.id);
                if (product) openAddStockSheet(product);
              },
              { failure: 'Zusammenführen fehlgeschlagen' },
            ),
          },
          [
            el('div', { class: 'item-body' }, [
              el(
                'div',
                { class: 'item-name' },
                candidate.brand ? `${candidate.name} · ${candidate.brand}` : candidate.name,
              ),
              el(
                'div',
                { class: 'item-meta' },
                `${Math.round(candidate.score * 100)} % Übereinstimmung`,
              ),
            ]),
            icon('merge', { size: 18 }),
          ],
        ),
      );
    }
    body.append(
      el('h3', { class: 'eyebrow' }, 'Schon im Vorrat?'),
      el(
        'p',
        { class: 'tiny' },
        'Dann lieber zusammenführen, statt ein zweites Produkt anzulegen.',
      ),
      list,
    );
  }

  const sheet = openSheet(body, { title: 'Neues Produkt' });

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            if (!name.value.trim()) {
              toast('Bitte einen Namen eintragen');
              return;
            }
            const size =
              sizeValue.value && sizeUnit.value
                ? toBaseSize(sizeValue.value, sizeUnit.value)
                : { value: null, unit: null };

            const { product } = await api<{ product: ProductView }>('/api/products', {
              method: 'POST',
              body: {
                name: name.value.trim(),
                brand: brand.value.trim() || null,
                barcode: code,
                category: off?.category ?? null,
                packageSizeValue: size.value,
                packageSizeUnit: size.unit,
                defaultUnit: size.unit,
                confirmed: true,
                imageUrl: off?.imageUrl ?? null,
                // Der volle Originalname bleibt als Erkennungshilfe erhalten.
                alias: off?.name && off.name !== name.value.trim() ? off.name : null,
                aliasSource: 'manual',
              },
            });
            sheet.close();
            await refreshProducts();
            void refreshExpiring();
            openAddStockSheet(product);
          },
          { failure: 'Anlegen fehlgeschlagen' },
        ),
      },
      'Anlegen & Bestand buchen',
    ),
  );
}

/** Produkt ohne Barcode von Hand anlegen. */
export function openNewProductForm(): void {
  const name = el('input', { class: 'input', type: 'text', placeholder: 'z.B. Vollmilch' });
  const sizeValue = el('input', {
    class: 'input',
    type: 'text',
    inputmode: 'decimal',
    placeholder: 'z.B. 1000',
  });
  const sizeUnit = el('select', { class: 'select' });
  for (const u of ['', 'Stück', 'ml', 'l', 'g', 'kg']) {
    sizeUnit.append(el('option', { value: u }, u || '—'));
  }

  const body = el('div', {}, [
    el('div', { class: 'field' }, [el('label', {}, 'Name'), name]),
    el('div', { class: 'field' }, [
      el('label', {}, 'Packungsgröße (optional)'),
      el('div', { class: 'input-row' }, [sizeValue, sizeUnit]),
    ]),
    el(
      'p',
      { class: 'field-hint' },
      'Kategorie, Meldebestand und Lagerort lassen sich danach im Vorrat ergänzen.',
    ),
  ]);

  const sheet = openSheet(body, { title: 'Produkt anlegen' });

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            if (!name.value.trim()) {
              toast('Bitte einen Namen eintragen');
              return;
            }
            const size =
              sizeValue.value && sizeUnit.value
                ? toBaseSize(sizeValue.value, sizeUnit.value)
                : { value: null, unit: null };
            const { product } = await api<{ product: ProductView }>('/api/products', {
              method: 'POST',
              body: {
                name: name.value.trim(),
                packageSizeValue: size.value,
                packageSizeUnit: size.unit,
                defaultUnit: size.unit,
              },
            });
            upsertProduct(product);
            sheet.close();
          },
          { success: 'Produkt angelegt', failure: 'Anlegen fehlgeschlagen' },
        ),
      },
      'Anlegen',
    ),
  );
}

export { clear };
